import { Loader2, Trash2, UserPlus } from "lucide-react"
import * as React from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"

import { PageHeader } from "@/components/layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { useLocale } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import type { WorkspaceMember, WorkspaceRole } from "@/lib/types"
import { formatDateTime } from "@/lib/utils"

const ROLES: WorkspaceRole[] = ["member", "admin", "owner"]

/**
 * Admin-only management of any workspace's members, direct (no invite flow). Lists
 * members, adds a user by email with a role, and re-roles or removes existing ones.
 * Reached from a workspace row on the admin user-detail page.
 */
export function AdminWorkspaceMembersPage() {
  const { workspaceId } = useParams()
  const navigate = useNavigate()
  const { locale } = useLocale()
  const [members, setMembers] = React.useState<WorkspaceMember[]>([])
  const [loading, setLoading] = React.useState(true)
  const [email, setEmail] = React.useState("")
  const [addRole, setAddRole] = React.useState<WorkspaceRole>("member")
  const [adding, setAdding] = React.useState(false)
  const [busy, setBusy] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    if (!workspaceId) return
    try {
      setMembers(await api.adminWorkspaceMembers(workspaceId))
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Could not load members.")
    } finally {
      setLoading(false)
    }
  }, [workspaceId])

  React.useEffect(() => {
    void load()
  }, [load])

  if (!workspaceId) return null

  async function add(event: React.FormEvent) {
    event.preventDefault()
    if (!workspaceId) return
    setAdding(true)
    try {
      await api.adminAddWorkspaceMember(workspaceId, { email: email.trim(), role: addRole })
      toast.success(`Added ${email.trim()} as ${addRole}.`)
      setEmail("")
      setAddRole("member")
      await load()
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Could not add the member.")
    } finally {
      setAdding(false)
    }
  }

  async function changeRole(member: WorkspaceMember, role: WorkspaceRole) {
    if (!workspaceId || role === member.role) return
    setBusy(member.user_id)
    try {
      await api.adminUpdateWorkspaceMemberRole(workspaceId, member.user_id, role)
      toast.success(`${member.email} is now ${role}.`)
      await load()
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Could not change the role.")
    } finally {
      setBusy(null)
    }
  }

  async function remove(member: WorkspaceMember) {
    if (!workspaceId) return
    if (!confirm(`Remove ${member.email} from this workspace?`)) return
    setBusy(member.user_id)
    try {
      await api.adminRemoveWorkspaceMember(workspaceId, member.user_id)
      toast.success(`${member.email} removed.`)
      await load()
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Could not remove the member.")
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <PageHeader
        title="Workspace members"
        description="Add, re-role, or remove members of this workspace directly."
        action={
          <Button variant="outline" onClick={() => navigate(-1)}>
            Back
          </Button>
        }
      />

      <div className="space-y-6 p-6">
        <form onSubmit={add} className="flex flex-wrap items-end gap-3 rounded-lg border p-4">
          <div className="min-w-56 flex-1 space-y-2">
            <Label htmlFor="member-email">Add member by email</Label>
            <Input
              id="member-email"
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="person@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="member-role">Role</Label>
            <Select value={addRole} onValueChange={(value) => setAddRole(value as WorkspaceRole)}>
              <SelectTrigger id="member-role" className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((role) => (
                  <SelectItem key={role} value={role}>
                    {role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={adding}>
            {adding ? <Loader2 className="size-4 animate-spin" /> : <UserPlus className="size-4" />}
            Add
          </Button>
        </form>

        {loading ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Email</th>
                  <th className="px-4 py-2 text-left font-medium">Role</th>
                  <th className="px-4 py-2 text-left font-medium">Joined</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.user_id} className="border-t">
                    <td className="px-4 py-2">
                      {member.email}
                      {member.display_name ? (
                        <span className="text-muted-foreground"> · {member.display_name}</span>
                      ) : null}
                      {member.is_default && (
                        <Badge variant="outline" className="ml-2">
                          default
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <Select
                        value={member.role}
                        onValueChange={(value) => changeRole(member, value as WorkspaceRole)}
                      >
                        <SelectTrigger className="w-32" disabled={busy === member.user_id}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map((role) => (
                            <SelectItem key={role} value={role}>
                              {role}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="text-muted-foreground px-4 py-2">
                      {formatDateTime(member.joined_at, locale)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        disabled={busy === member.user_id}
                        onClick={() => remove(member)}
                      >
                        <Trash2 className="size-4" />
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
